<template>
  <div
    class="flex custom_datepicker_spm"
    :class=" { 'flex-column': calendarPosition === 'bottom', 'flex-row': calendarPosition === 'right', 'custom_range': chosenInterval === IntervalDateEnum.CUSTOM_DATE_RANGE }"
  >
    <div class="flex">
      <SpmOverlayPanel
        :class-trigger="'flex justify-content-between flex-wrap custom_datepicker_input ' +
          (chosenInterval === IntervalDateEnum.CUSTOM_DATE_RANGE ? IntervalDateEnum.CUSTOM_DATE_RANGE : '')"
        class-header="flex align-items-center profile-info justify-content-left"
        class-content="flex justify-content-center spm-overlay-panel-content"
        class-footer="flex align-items-center justify-content-center spm-overlay-panel-content"
        style-overlay-panel="margin-top:0px;"
        :close-panel="closePanel"
        @onclose-panel="closePanel = false"
      >
        <template #trigger>
          <div class="flex align-items-center justify-content-center">
            <i class="far fa-calendar-alt mr-2" />
            <span>{{ t("customDatePicker."+chosenInterval) }}</span>
          </div>
          <div class="flex align-items-center justify-content-center">
            <i class="far fa-chevron-down" />
          </div>
        </template>
        <SpmPanelMenu
          :items="optionsItems"
        />
      </SpmOverlayPanel>
    </div>
    <div class="flex">
      <Calendar
        v-if="chosenInterval === IntervalDateEnum.CUSTOM_DATE_RANGE"
        v-model="customDate"
        date-format="dd/mm/yy"
        selection-mode="range"
        :hide-on-range-selection="true"
        :manual-input="manualInput"
        :number-of-months="numberOfMonths"
        :panel-class="resolvedPanelClass"
        :min-date="minDate ?? undefined"
        :max-date="maxDate ?? undefined"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, PropType, Ref, ref, SetupContext, watch,
} from 'vue';
import Calendar from 'primevue/calendar';
import SpmOverlayPanel from '@/components/spm-primevue/SpmOverlayPanel.vue';
import SpmPanelMenu from '@/components/spm-primevue/SpmPanelMenu.vue';
import { IntervalDateEnum, PrimvueMenuModel } from '@/types';
import { useI18n } from 'vue-i18n';
import moment from 'moment';

export default defineComponent({
  name: 'CustomDatePicker',

  components: {
    Calendar,
    SpmOverlayPanel,
    SpmPanelMenu,
  },

  props: {
    modelValue: {
      type: Object,
      default: () => Object,
    },

    calendarPosition: {
      type: String,
      default: 'bottom',
    },

    noLimit: {
      type: Boolean,
      required: false,
      default: false,
    },

    customMinDate: {
      type: Number,
      required: false,
      default: 0,
    },

    customOptionsItems: {
      type: Array as PropType<any[]>,
      required: false,
      default: () => ([]),
    },

    updateValue: {
      type: Boolean,
      required: false,
      default: false,
    },

    defaultValue: {
      type: String,
      required: false,
      default: '',
    },

    // Nombre de mois affichés côte à côte dans le Calendar quand
    // « Période personnalisée » est choisi. Default = 1 pour conserver
    // le comportement historique sur tous les usages existants.
    numberOfMonths: {
      type: Number,
      required: false,
      default: 1,
    },

    // Active la saisie clavier dans l'input Calendar (format dd/mm/yy).
    // Default = false pour rester aligné sur l'UX historique.
    manualInput: {
      type: Boolean,
      required: false,
      default: false,
    },

    // Classe CSS appliquée au panel/pop-up primevue du Calendar.
    // Sert aux écrans qui veulent restyler le calendrier (densité, couleurs)
    // sans toucher au composant central. Default '' = pas de classe ajoutée.
    panelClass: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: ['update:modelValue', 'reset-update-value', 'value-choosed'],

  setup(props: { modelValue: any; customMinDate: number; noLimit: boolean; customOptionsItems: IntervalDateEnum[]; updateValue: boolean; defaultValue: string; numberOfMonths: number; manualInput: boolean; panelClass: string; }, { emit }: SetupContext) {
    const { t } = useI18n();

    const closePanel = ref(false);
    const chosenInterval: Ref<IntervalDateEnum|'empty'|string> = ref('empty');

    const customDate = ref(props.modelValue.customDateRange && props.modelValue.customDateRange.length > 0
      ? props.modelValue.customDateRange.map((date: string) => moment(date).toDate())
      : [moment().subtract(30, 'days').toDate(), moment().toDate()]);

    if (props.modelValue.interval) {
      chosenInterval.value = props.modelValue.interval;
    }
    if (props.defaultValue) {
      chosenInterval.value = props.defaultValue;
    }

    // Limit the date range to a maximum of one year apart
    const maxDate = ref(props.noLimit ? null : moment().toDate());
    const minDate = ref(!props.customMinDate ? null : moment().subtract(props.customMinDate, 'years').toDate());
    const optionsItems = ref<any[]>([]);

    if (props.customOptionsItems.length > 0) {
      optionsItems.value = props.customOptionsItems.map((key) => ({
          label: t(`customDatePicker.${key}`),
          command: () => {
            chosenInterval.value = key;
            closePanel.value = true;
            emit('value-choosed');
          },
          class: computed(() => (chosenInterval.value === key ? 'activeMenuItem' : '')),
        }));
    } else {
      optionsItems.value = Object.values(IntervalDateEnum).map((key) => ({
        label: t(`customDatePicker.${key}`),
        command: () => {
          chosenInterval.value = key;
          closePanel.value = true;
          emit('value-choosed');
        },
        class: computed(() => (chosenInterval.value === key ? 'activeMenuItem' : '')),
      }));
    }

    // add empty value to the optionsItems
    optionsItems.value.unshift({
      label: t('customDatePicker.empty'),
      command: () => {
        chosenInterval.value = 'empty';
        closePanel.value = true;
        emit('value-choosed');
      },
      class: computed(() => (chosenInterval.value === 'empty' ? 'activeMenuItem' : '')),
    });

    // Borne MAX historique (= today si pas de noLimit). Conservée pour
    // pouvoir y revenir quand l'utilisateur sort du mode customDateRange.
    const baseMaxDate = computed(() => (props.noLimit ? null : moment().toDate()));

    // Recalcule maxDate à `start + 1 an` (capé à today) tant qu'une
    // sélection custom est active. Utilisé sur les deux phases (1er
    // click sans 2e click, ET sélection complète) pour empêcher qu'un
    // re-click ultérieur dépasse la fenêtre de 12 mois — ce qui était
    // possible avant car maxDate était réinitialisé à today après le 2e
    // click.
    const applyRangeMaxBound = (anchorStart: Date) => {
      const oneYearLater = moment(anchorStart).add(1, 'years').toDate();
      const todayDate = moment().toDate();
      maxDate.value = oneYearLater > todayDate && !props.noLimit
        ? todayDate
        : oneYearLater;
    };

    watch([chosenInterval, customDate], (newVal) => {
      if (chosenInterval.value === 'customDateRange') {
        if (customDate.value[0] && !customDate.value[1]) {
          applyRangeMaxBound(customDate.value[0]);
        } else if (customDate.value[0] && customDate.value[1]) {
          // Sélection complète : on garde la contrainte (start + 1 an)
          // pour qu'un éventuel re-click ne permette pas de dépasser
          // 12 mois lors d'une nouvelle sélection.
          applyRangeMaxBound(customDate.value[0]);
          emit('update:modelValue', {
            interval: chosenInterval.value,
            customDateRange: customDate.value,
          });
        }
      } else {
        // Hors mode custom : on relâche la contrainte → max revient
        // à la borne par défaut (today ou null si noLimit).
        maxDate.value = baseMaxDate.value;
        emit('update:modelValue', {
          interval: chosenInterval.value,
        });
      }
      if (props.updateValue) {
        emit('reset-update-value');
      }
    }, { deep: true });

    watch(() => props.updateValue, () => {
      if (props.updateValue) {
        customDate.value = props.modelValue.customDateRange && props.modelValue.customDateRange.length > 0
        ? props.modelValue.customDateRange.map((date: string) => moment(date).toDate())
        : [moment().subtract(30, 'days').toDate(), moment().toDate()];
        if (props.modelValue.interval) {
          chosenInterval.value = props.modelValue.interval;
        }
      }
    }, { deep: true });

    onMounted(() => {
      if (chosenInterval.value === 'customDateRange') {
        if (customDate.value[0] && customDate.value[1]) {
          emit('update:modelValue', {
            interval: chosenInterval.value,
            customDateRange: customDate.value,
          });
        }
      } else {
        // on load: update the value for the parent
        emit('update:modelValue', {
          interval: chosenInterval.value,
        });
      }
    });

    // Concatène la classe par défaut `custom-datepicker-panel` (cible le
    // style global compact ci-dessous) avec la `panelClass` optionnelle
    // passée par le caller. Permet aux consommateurs d'ajouter leurs
    // propres modifiers sans perdre la densification de base.
    const resolvedPanelClass = computed(() => {
      const base = 'custom-datepicker-panel';
      return props.panelClass ? `${base} ${props.panelClass}` : base;
    });

    return {
      t,
      IntervalDateEnum,
      chosenInterval,
      optionsItems,
      minDate,
      maxDate,
      customDate,
      closePanel,
      resolvedPanelClass,
    };
  },

});
</script>

<style lang="scss">
.custom_datepicker_spm {
  .custom_datepicker_input {
    padding: 0.5rem 0.5rem;
    border: 1px solid #ced4da;
    cursor: pointer;
    border-radius: 0.3rem;
    transition: background-color 0.2s, color 0.2s, border-color 0.2s, box-shadow 0.2s;
    color: #6c757d;
    width: 100%;
    background:$white;
    font-weight: normal;

    .flex:first-child {
      margin-right: 0.5rem;
    }
    .flex:last-child {
      margin-right: 0.3rem;
    }
  }

  .p-calendar {
    width: 100%;
    input {
      width: 100% !important;
      background-color: #f8f9fa !important;
    }
  }

  &.flex-row.custom_range {
    .custom_datepicker_input {
      border-top-right-radius: 0px;
      border-bottom-right-radius: 0px;
      border-right: none;
    }
    .p-calendar input {
      border-top-left-radius: 0px;
      border-bottom-left-radius: 0px;
      background:$white;
    }
  }

  &.flex-column.custom_range {
    .custom_datepicker_input {
      border-bottom-left-radius: 0px;
      border-bottom-right-radius: 0px;
    }
    .p-calendar input {
      border-top-left-radius: 0px;
      border-top-right-radius: 0px;
      border-top: none;
      background:$white;
    }
  }

  .p-menu-list {
    .p-menuitem {
      padding: 0.1rem 0.2rem;

      &.activeMenuItem {
        .p-menuitem-content {
          background-color: #e9ecef;
        }
      }

      .p-menuitem-content {
        border-radius: 0.3rem;
        font-size: 0.875rem;
        font-weight: 600;
        color: $tuna;
      }
    }
  }
}
</style>

<!--
  Style NON-scoped : la pop-up Calendar primevue est rendue hors du scope
  du composant (panel teleporté), donc seul un style global peut l'atteindre.
  Préfixé par `.custom-datepicker-panel` (classe injectée systématiquement
  via `resolvedPanelClass`) pour ne cibler QUE les panels rendus par
  CustomDatePicker — pas les autres usages de primevue Calendar dans
  l'application.
-->
<style lang="scss">
.custom-datepicker-panel {
  font-size: 0.8rem;

  // En-tête (chevrons + mois/année).
  .p-datepicker-header {
    padding: 0.35rem 0.5rem;
    font-size: 0.85rem;
  }

  .p-datepicker-prev,
  .p-datepicker-next {
    width: 1.5rem;
    height: 1.5rem;

    .p-icon {
      width: 0.85rem;
      height: 0.85rem;
    }
  }

  .p-datepicker-title {
    gap: 0.4rem;

    .p-datepicker-month,
    .p-datepicker-year {
      font-size: 0.85rem;
      font-weight: 600;
    }
  }

  // Groupes de mois quand number-of-months > 1 : un peu d'air entre
  // les deux mois sans gaspiller la largeur.
  .p-datepicker-group-container {
    gap: 0.5rem;
  }

  .p-datepicker-group {
    padding: 0.35rem 0.4rem;
  }

  // Table : on densifie en réduisant la taille des cellules et les paddings.
  .p-datepicker-calendar {
    th {
      padding: 0.25rem 0.1rem;
      font-size: 0.65rem;
      font-weight: 600;
      color: #6b7280;
      text-transform: uppercase;
    }

    td {
      padding: 0.1rem;
    }

    td > span {
      width: 1.85rem;
      height: 1.85rem;
      font-size: 0.78rem;
      border-radius: 999px;
      transition: background-color 0.12s ease, color 0.12s ease;
    }
  }
}
</style>
